// src/components/AuthModal.js
import React, { useState, useEffect } from 'react';
import AuthService from '../services/authService';
import './AuthModal.css';

const AuthModal = ({ isOpen, onClose, onSuccess, userType = 'freelancer' }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    confirmPassword: '',
    companyName: '', // For businesses
    industry: '', // For businesses
    professionalTitle: '', // For freelancers
    skills: '', // For freelancers
    hourlyRate: '' // For freelancers
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [serviceStatus, setServiceStatus] = useState('checking');

  // Check service health when modal opens
  useEffect(() => {
    if (isOpen) {
      checkServiceHealth();
    }
  }, [isOpen]);

  const checkServiceHealth = async () => {
    try {
      const isHealthy = await AuthService.checkServiceHealth();
      setServiceStatus(isHealthy ? 'healthy' : 'unavailable');
    } catch (error) {
      setServiceStatus('unavailable');
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleGoogleSignIn = async () => {
    if (serviceStatus === 'unavailable') {
      setError('Our authentication service is currently unavailable. Please try again later.');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      console.log('Attempting Google sign-in as', userType);
      const response = await AuthService.signInWithGoogle();
      
      // Handle redirect case
      if (response && response.redirected) {
        setMessage('Redirecting to complete sign-in...');
        return;
      }
      
      // Add user type to response
      const userData = {
        ...response.user,
        accountType: userType
      };
      
      console.log('Google sign-in response:', userData);
      setMessage('Google sign-in successful!');
      
      setTimeout(() => {
        onSuccess(userData);
        onClose();
        resetForm();
      }, 1000);
      
    } catch (err) {
      console.error('Google sign-in error:', err);
      
      if (err.message.includes('Redirecting')) {
        setMessage(err.message);
        setError('');
      } else if (err.message.includes('unavailable') || err.message.includes('503')) {
        setError('Authentication service is temporarily unavailable. Please try again in a few minutes.');
        setServiceStatus('unavailable');
      } else if (err.message.includes('cancelled')) {
        setError('Sign-in was cancelled. Please try again.');
      } else if (err.message.includes('blocked')) {
        setError('Popup was blocked. Please allow popups for this site and try again.');
      } else {
        setError(err.message || 'Google sign-in failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (serviceStatus === 'unavailable') {
      setError('Our authentication service is currently unavailable. Please try again later.');
      return;
    }

    setLoading(true);
    setError('');
    setMessage('');

    try {
      if (isLogin) {
        console.log('Attempting login as', userType);
        const response = await AuthService.login({
          email: formData.email,
          password: formData.password
        });
        
        // Add user type to response
        const userData = {
          ...response.user,
          accountType: userType
        };
        
        console.log('Login response:', userData);
        setMessage('Login successful!');
        
        window.dispatchEvent(new CustomEvent('authChange', { 
          detail: { type: 'login', user: userData } 
        }));
        
        setTimeout(() => {
          onSuccess(userData);
          onClose();
          resetForm();
        }, 1000);
        
      } else {
        // Register
        if (formData.password !== formData.confirmPassword) {
          setError('Passwords do not match');
          setLoading(false);
          return;
        }

        if (formData.password.length < 6) {
          setError('Password must be at least 6 characters');
          setLoading(false);
          return;
        }

        // Validate business-specific fields
        if (userType === 'business' && !formData.companyName) {
          setError('Company name is required for business accounts');
          setLoading(false);
          return;
        }

        console.log('Attempting registration as', userType);
        const response = await AuthService.register({
          email: formData.email,
          password: formData.password,
          firstName: formData.firstName,
          lastName: formData.lastName,
          accountType: userType,
          companyName: formData.companyName,
          industry: formData.industry,
          professionalTitle: formData.professionalTitle,
          skills: formData.skills,
          hourlyRate: formData.hourlyRate
        });
        
        console.log('Registration response:', response);
        setMessage('Registration successful! Please check your email to verify your account.');
        
        setTimeout(() => {
          setIsLogin(true);
          setFormData({
            email: formData.email,
            password: '',
            firstName: '',
            lastName: '',
            confirmPassword: '',
            companyName: '',
            industry: '',
            professionalTitle: '',
            skills: '',
            hourlyRate: ''
          });
          setMessage('');
        }, 3000);
      }
    } catch (err) {
      console.error('Authentication error:', err);
      
      if (err.message.includes('unavailable') || err.message.includes('503')) {
        setError('Our servers are temporarily unavailable. Please try again in a few minutes.');
        setServiceStatus('unavailable');
      } else {
        setError(err.message);
      }
    }
    
    setLoading(false);
  };

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email) {
      setError('Please enter your email address');
      return;
    }

    if (serviceStatus === 'unavailable') {
      setError('Our password reset service is currently unavailable. Please try again later.');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      console.log('Sending password reset email...');
      await AuthService.resetPassword(formData.email);
      
      setMessage('Password reset link has been sent to your email address. Please check your inbox and spam folder.');
      
      setTimeout(() => {
        setShowForgotPassword(false);
        setMessage('You can now sign in with your new password.');
        setTimeout(() => setMessage(''), 3000);
      }, 5000);
      
    } catch (err) {
      console.error('Forgot password error:', err);
      
      if (err.message.includes('unavailable') || err.message.includes('503')) {
        setError('Password reset service is temporarily unavailable. Please try again later.');
        setServiceStatus('unavailable');
      } else {
        setError(err.message || 'Failed to send reset email. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      confirmPassword: '',
      companyName: '',
      industry: '',
      professionalTitle: '',
      skills: '',
      hourlyRate: ''
    });
    setError('');
    setMessage('');
    setShowForgotPassword(false);
  };

  const switchMode = (loginMode) => {
    setIsLogin(loginMode);
    setShowForgotPassword(false);
    resetForm();
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleForgotPassword = () => {
    setShowForgotPassword(true);
    setError('');
    setMessage('');
  };

  const handleBackToLogin = () => {
    setShowForgotPassword(false);
    setError('');
    setMessage('');
  };

  const handleRetryConnection = () => {
    setServiceStatus('checking');
    checkServiceHealth();
  };

  if (!isOpen) return null;

  // Customize text based on user type
  const titles = {
    business: {
      login: 'Business Portal',
      register: 'Register Your Business',
      subtitle: {
        login: 'Post RFQs and hire verified freelancers',
        register: 'Start posting RFQs and finding top talent'
      }
    },
    freelancer: {
      login: 'Freelancer Portal',
      register: 'Join as Freelancer',
      subtitle: {
        login: 'Browse RFQs and submit proposals',
        register: 'Find work opportunities and build your portfolio'
      }
    }
  };

  const currentTitles = titles[userType] || titles.freelancer;

  return (
    <div className="auth-overlay" onClick={handleClose}>
      <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
        <div className="auth-header">
          <div className="auth-type-badge">
            {userType === 'business' ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <rect x="2" y="7" width="20" height="14" rx="2" ry="2" stroke="#003d7a" strokeWidth="2"/>
                <path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16" stroke="#003d7a" strokeWidth="2"/>
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="#003d7a" strokeWidth="2"/>
                <path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke="#003d7a" strokeWidth="2"/>
              </svg>
            )}
            <span>{userType === 'business' ? 'Business' : 'Freelancer'}</span>
          </div>
          
          <h2>
            {showForgotPassword 
              ? 'Reset Password' 
              : (isLogin ? currentTitles.login : currentTitles.register)
            }
          </h2>
          <p className="auth-subtitle">
            {showForgotPassword
              ? 'Enter your email address to receive a password reset link'
              : (isLogin 
                ? currentTitles.subtitle.login
                : currentTitles.subtitle.register
              )
            }
          </p>
          <button className="auth-close" onClick={handleClose}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Service Status Indicator */}
        {serviceStatus === 'unavailable' && (
          <div className="service-status unavailable">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <path d="m15 9-6 6"/>
              <path d="m9 9 6 6"/>
            </svg>
            <div>
              <strong>Service Temporarily Unavailable</strong>
              <p>Our authentication service is currently experiencing issues. Please try again in a few minutes.</p>
              <button 
                type="button" 
                onClick={handleRetryConnection}
                className="retry-btn"
              >
                Retry Connection
              </button>
            </div>
          </div>
        )}

        {serviceStatus === 'checking' && (
          <div className="service-status checking">
            <div className="loading-spinner"></div>
            <span>Checking service status...</span>
          </div>
        )}

        {showForgotPassword ? (
          // Forgot Password Form
          <form onSubmit={handleForgotPasswordSubmit} className="auth-form">
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="john@example.com"
                autoComplete="email"
                disabled={serviceStatus === 'unavailable'}
              />
            </div>

            {error && (
              <div className="error-message">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="15" y1="9" x2="9" y2="15"/>
                  <line x1="9" y1="9" x2="15" y2="15"/>
                </svg>
                {error}
              </div>
            )}

            {message && (
              <div className="success-message">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22,4 12,14.01 9,11.01"/>
                </svg>
                {message}
              </div>
            )}

            <button
              type="submit"
              className="auth-submit-btn"
              disabled={loading || serviceStatus === 'unavailable'}
            >
              {loading ? (
                <span className="loading-text">
                  <span className="loading-spinner"></span>
                  Sending...
                </span>
              ) : (
                'Send Reset Link'
              )}
            </button>

            <div className="auth-switch">
              <p>
                Remember your password?{' '}
                <button
                  type="button"
                  onClick={handleBackToLogin}
                  className="auth-switch-btn"
                  disabled={loading}
                >
                  Back to Sign In
                </button>
              </p>
            </div>
          </form>
        ) : (
          <>
            {/* Google Sign-In Button - Only show for login */}
            {isLogin && serviceStatus === 'healthy' && (
              <div className="google-signin-section">
                <button
                  onClick={handleGoogleSignIn}
                  disabled={loading}
                  className="google-signin-btn"
                >
                  <svg viewBox="0 0 24 24" className="google-icon">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  {loading ? (
                    <span className="loading-text">
                      <span className="loading-spinner"></span>
                      Signing in...
                    </span>
                  ) : (
                    `Continue with Google`
                  )}
                </button>
                
                <div className="divider">
                  <span>or</span>
                </div>
              </div>
            )}

            {/* Regular Auth Form */}
            <form onSubmit={handleSubmit} className="auth-form">
              {!isLogin && (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="firstName">First Name</label>
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        required
                        placeholder="John"
                        autoComplete="given-name"
                        disabled={serviceStatus === 'unavailable'}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="lastName">Last Name</label>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                        placeholder="Doe"
                        autoComplete="family-name"
                        disabled={serviceStatus === 'unavailable'}
                      />
                    </div>
                  </div>

                  {/* Business-specific fields */}
                  {userType === 'business' && (
                    <>
                      <div className="form-group">
                        <label htmlFor="companyName">Company Name *</label>
                        <input
                          type="text"
                          id="companyName"
                          name="companyName"
                          value={formData.companyName}
                          onChange={handleChange}
                          required
                          placeholder="Your Company Name"
                          autoComplete="organization"
                          disabled={serviceStatus === 'unavailable'}
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="industry">Industry</label>
                        <select
                          id="industry"
                          name="industry"
                          value={formData.industry}
                          onChange={handleChange}
                          disabled={serviceStatus === 'unavailable'}
                        >
                          <option value="">Select industry...</option>
                          <option value="technology">Technology</option>
                          <option value="finance">Finance & Banking</option>
                          <option value="healthcare">Healthcare</option>
                          <option value="retail">Retail & E-commerce</option>
                          <option value="manufacturing">Manufacturing</option>
                          <option value="construction">Construction</option>
                          <option value="education">Education</option>
                          <option value="hospitality">Hospitality & Tourism</option>
                          <option value="professional-services">Professional Services</option>
                          <option value="nonprofit">Non-Profit</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                    </>
                  )}

                  {/* Freelancer-specific fields */}
                  {userType === 'freelancer' && (
                    <>
                      <div className="form-group">
                        <label htmlFor="professionalTitle">Professional Title</label>
                        <input
                          type="text"
                          id="professionalTitle"
                          name="professionalTitle"
                          value={formData.professionalTitle}
                          onChange={handleChange}
                          placeholder="e.g., Full Stack Developer, Graphic Designer"
                          disabled={serviceStatus === 'unavailable'}
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="skills">Primary Skills / Categories</label>
                        <select
                          id="skills"
                          name="skills"
                          value={formData.skills}
                          onChange={handleChange}
                          disabled={serviceStatus === 'unavailable'}
                        >
                          <option value="">Select primary skill...</option>
                          <option value="web-development">Web Development</option>
                          <option value="graphic-design">Graphic Design</option>
                          <option value="software-development">Software Development</option>
                          <option value="digital-marketing">Digital Marketing</option>
                          <option value="writing-content">Writing & Content</option>
                          <option value="data-analytics">Data Analytics</option>
                          <option value="bookkeeping">Bookkeeping & Accounting</option>
                          <option value="legal-services">Legal Services</option>
                          <option value="it-support">IT Support</option>
                          <option value="photography-video">Photography & Video</option>
                          <option value="consulting">Consulting</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label htmlFor="hourlyRate">Hourly Rate (Optional)</label>
                        <input
                          type="number"
                          id="hourlyRate"
                          name="hourlyRate"
                          value={formData.hourlyRate}
                          onChange={handleChange}
                          placeholder="e.g., 500"
                          min="0"
                          step="50"
                          disabled={serviceStatus === 'unavailable'}
                        />
                        <div className="password-hint">
                          Enter your hourly rate in ZAR (South African Rands)
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}

              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="john@example.com"
                  autoComplete="email"
                  disabled={serviceStatus === 'unavailable'}
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  minLength="6"
                  placeholder="••••••••"
                  autoComplete={isLogin ? "current-password" : "new-password"}
                  disabled={serviceStatus === 'unavailable'}
                />
                {!isLogin && (
                  <div className="password-hint">
                    Must be at least 6 characters long
                  </div>
                )}
              </div>

              {!isLogin && (
                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm Password</label>
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                    minLength="6"
                    placeholder="••••••••"
                    autoComplete="new-password"
                    disabled={serviceStatus === 'unavailable'}
                  />
                </div>
              )}

              {error && (
                <div className="error-message">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="15" y1="9" x2="9" y2="15"/>
                    <line x1="9" y1="9" x2="15" y2="15"/>
                  </svg>
                  {error}
                </div>
              )}

              {message && (
                <div className="success-message">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                    <polyline points="22,4 12,14.01 9,11.01"/>
                  </svg>
                  {message}
                </div>
              )}

              <button
                type="submit"
                className="auth-submit-btn"
                disabled={loading || serviceStatus === 'unavailable'}
              >
                {loading ? (
                  <span className="loading-text">
                    <span className="loading-spinner"></span>
                    Processing...
                  </span>
                ) : (
                  isLogin ? 'Sign In' : 'Create Account'
                )}
              </button>
            </form>

            <div className="auth-switch">
              {isLogin ? (
                <p>
                  Don't have an account?{' '}
                  <button
                    type="button"
                    onClick={() => switchMode(false)}
                    className="auth-switch-btn"
                    disabled={loading}
                  >
                    Sign up
                  </button>
                </p>
              ) : (
                <p>
                  Already have an account?{' '}
                  <button
                    type="button"
                    onClick={() => switchMode(true)}
                    className="auth-switch-btn"
                    disabled={loading}
                  >
                    Sign in
                  </button>
                </p>
              )}
            </div>

            {isLogin && (
              <div className="forgot-password">
                <button
                  type="button"
                  className="forgot-password-btn"
                  onClick={handleForgotPassword}
                  disabled={loading}
                >
                  Forgot your password?
                </button>
              </div>
            )}
          </>
        )}

        <div className="auth-security">
          <div className="security-badge">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <circle cx="12" cy="16" r="1"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
            <span>Secure & Encrypted</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthModal;